import type { FilterResult } from '@components/Home/HomePageSearchModal'
import { getNativeReaderHref } from 'helpers/nativeReaderHref'
import Link from 'next/link'
import type { IconType } from 'react-icons'
import { FaBookOpen, FaMosque, FaThLarge } from 'react-icons/fa'
import { HiChevronRight } from 'react-icons/hi'
import type { SearchResultGroup, SearchResultKind } from './searchResultGroups'

type SearchResultListProps = {
  groups: SearchResultGroup[]
  /** Index of the keyboard-selected result, counted across all groups. */
  activeIndex: number
  onResultHover: (_index: number) => void
}

const KIND_ICONS: Record<SearchResultKind, IconType> = {
  quran: FaBookOpen,
  dua: FaMosque,
  tool: FaThLarge,
}

/** The DOM id of a result row, used to scroll the keyboard selection into view. */
export const getSearchResultId = (index: number) => `search-result-${index}`

type ResultRowProps = {
  result: FilterResult
  kind: SearchResultKind
  index: number
  isActive: boolean
  onHover: (_index: number) => void
}

/** One result: a type icon, the result name and an arrow. */
const ResultRow = ({ result, kind, index, isActive, onHover }: ResultRowProps) => {
  const Icon = KIND_ICONS[kind]
  return (
    <li id={getSearchResultId(index)}>
      <Link
        href={getNativeReaderHref(result.url)}
        onMouseMove={() => onHover(index)}
        data-active={isActive}
        className="flex min-h-12 items-center gap-3 rounded-xl px-3 py-2.5 transition-colors active:bg-surface-strong md:data-[active=true]:bg-surface-strong"
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Icon size={14} aria-hidden />
        </span>
        <span className="min-w-0 flex-1 text-[15px] font-medium text-main">
          {result.name}
        </span>
        <HiChevronRight size={18} className="shrink-0 text-muted" aria-hidden />
      </Link>
    </li>
  )
}

/**
 * Search results split into "Kur'an-ı Kerim", "Dualar" and "Araçlar" groups.
 * The keyboard selection moves through all groups as one list.
 */
const SearchResultList = ({
  groups,
  activeIndex,
  onResultHover,
}: SearchResultListProps) => {
  // Each group's first row index, so rows keep one running index across groups.
  const groupStartIndexes = groups.map((_, groupIndex) =>
    groups.slice(0, groupIndex).reduce((total, group) => total + group.results.length, 0),
  )

  return (
    <div className="space-y-4">
      {groups.map((group, groupIndex) => (
        <section key={group.kind}>
          <h3 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-muted">
            {group.title}
          </h3>
          <ul>
            {group.results.map((result, resultIndex) => {
              const index = groupStartIndexes[groupIndex] + resultIndex
              return (
                <ResultRow
                  key={`${result.url}-${result.name}`}
                  result={result}
                  kind={group.kind}
                  index={index}
                  isActive={index === activeIndex}
                  onHover={onResultHover}
                />
              )
            })}
          </ul>
        </section>
      ))}
    </div>
  )
}

export default SearchResultList
